/**
 * TBNC wordmark — faithful SVG recreation of the client's logo (green bar
 * "TAMPA BAY" flanked by checker clusters, "NETWORKING CALENDAR" beneath),
 * with the checkerboard reinterpreted as a calendar-grid motif. Crisp at any
 * size; `tone` picks the sub-line color for light or dark backgrounds.
 */

const GREEN = '#16A34A';
const GREEN_LT = '#B9E2C8';
const INK = '#0F2A2E';

function Cluster({ x, y }: { x: number; y: number }) {
  // 3x3 calendar-grid checker, 9px cells with 3px gutters (33px square).
  const cells = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      cells.push(
        <rect
          key={`${r}-${c}`}
          x={x + c * 12}
          y={y + r * 12}
          width={9}
          height={9}
          rx={1.5}
          fill={(r + c) % 2 === 0 ? GREEN : GREEN_LT}
        />
      );
    }
  }
  return <g>{cells}</g>;
}

export function Logo({
  tone = 'onLight',
  height = 44,
  // The wordmark artwork below is still Tampa's. Until the blueprint engine
  // draws a per-city mark, `title` at least gives screen readers the right
  // city's name rather than confidently the wrong one.
  title = 'Networking Calendar',
}: {
  tone?: 'onLight' | 'onDark';
  height?: number;
  title?: string;
}) {
  const sub = tone === 'onDark' ? '#FFFFFF' : INK;
  return (
    <svg
      viewBox="0 0 411 108"
      height={height}
      width={(411 / 108) * height}
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <Cluster x={0} y={14} />
      <Cluster x={378} y={14} />
      <rect x={45} y={4} width={321} height={54} rx={5} fill={GREEN} />
      <text
        x={205.5}
        y={42}
        textAnchor="middle"
        fill="#FFFFFF"
        fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
        fontWeight={800}
        fontSize={31}
        letterSpacing={6}
        textLength={281}
        lengthAdjust="spacingAndGlyphs"
      >
        TAMPA BAY
      </text>
      <text
        x={205.5}
        y={92}
        textAnchor="middle"
        fill={sub}
        fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
        fontWeight={700}
        fontSize={22}
        letterSpacing={3.5}
        textLength={385}
        lengthAdjust="spacingAndGlyphs"
      >
        NETWORKING CALENDAR
      </text>
    </svg>
  );
}

/** Square mark — favicon DNA at component scale (admin sidebar, 404, etc.). */
export function LogoMark({ size = 28 }: { size?: number }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
      <rect width="64" height="64" rx="14" fill={GREEN} />
      <g fill="#FFFFFF">
        <rect x="38" y="10" width="8" height="8" rx="1.5" />
        <rect x="46" y="18" width="8" height="8" rx="1.5" />
        <rect x="38" y="26" width="8" height="8" rx="1.5" opacity="0.45" />
        <rect x="46" y="10" width="8" height="8" rx="1.5" opacity="0.45" />
      </g>
      <rect x="10" y="38" width="44" height="6" rx="3" fill="#FFFFFF" />
      <rect x="10" y="48" width="28" height="6" rx="3" fill="#FFFFFF" opacity="0.55" />
    </svg>
  );
}
